import type { NativeBooking } from './native-booking-api';
import { nativeBookingStatusLabel } from './native-booking-presentation';

export type MyBookingsSortKey =
  | 'public_number'
  | 'slot_start_at'
  | 'site'
  | 'equipment'
  | 'status';

export type MyBookingsSortDir = 'asc' | 'desc';

export type MyBookingsSortState = {
  readonly key: MyBookingsSortKey;
  readonly dir: MyBookingsSortDir;
};

export const DEFAULT_MY_BOOKINGS_SORT: MyBookingsSortState = {
  key: 'slot_start_at',
  dir: 'asc',
};

const ruCollator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });

/** Business order aligned with status filter / pill semantics (not raw API enum order). */
const STATUS_BUSINESS_RANK: Record<string, number> = {
  'Ожидает осмотра': 10,
  'Осмотр начат': 20,
  Завершён: 30,
  Отменена: 40,
  Отменён: 40,
  'Резерв времени': 50,
  Черновик: 55,
  'Резерв истёк': 60,
};

function slotTimestampMs(iso: string): number | null {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function compareNullableNumber(
  left: number | null,
  right: number | null,
  dir: MyBookingsSortDir,
): number {
  if (left === null && right === null) {
    return 0;
  }
  // Invalid / missing dates always after valid ones (stable, no throw).
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  const delta = left - right;
  return dir === 'asc' ? delta : -delta;
}

function compareText(left: string, right: string, dir: MyBookingsSortDir): number {
  const cmp = ruCollator.compare(left, right);
  return dir === 'asc' ? cmp : -cmp;
}

function compareTieBreak(left: NativeBooking, right: NativeBooking): number {
  const byNumber = ruCollator.compare(left.public_number, right.public_number);
  if (byNumber !== 0) {
    return byNumber;
  }
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}

function statusSortParts(row: NativeBooking): { readonly rank: number; readonly label: string } {
  const label = nativeBookingStatusLabel(row);
  return {
    rank: STATUS_BUSINESS_RANK[label] ?? 100,
    label,
  };
}

export function compareMyBookings(
  left: NativeBooking,
  right: NativeBooking,
  sort: MyBookingsSortState,
  siteName: (row: NativeBooking) => string,
  equipmentName: (row: NativeBooking) => string,
): number {
  let primary = 0;
  switch (sort.key) {
    case 'public_number':
      primary = compareText(left.public_number, right.public_number, sort.dir);
      break;
    case 'slot_start_at':
      primary = compareNullableNumber(
        slotTimestampMs(left.slot_start_at),
        slotTimestampMs(right.slot_start_at),
        sort.dir,
      );
      break;
    case 'site':
      primary = compareText(siteName(left), siteName(right), sort.dir);
      break;
    case 'equipment':
      primary = compareText(equipmentName(left), equipmentName(right), sort.dir);
      break;
    case 'status': {
      const leftStatus = statusSortParts(left);
      const rightStatus = statusSortParts(right);
      const byRank = leftStatus.rank - rightStatus.rank;
      if (byRank !== 0) {
        primary = sort.dir === 'asc' ? byRank : -byRank;
      } else {
        primary = compareText(leftStatus.label, rightStatus.label, sort.dir);
      }
      break;
    }
    default: {
      const _exhaustive: never = sort.key;
      void _exhaustive;
      primary = 0;
    }
  }
  if (primary !== 0) {
    return primary;
  }
  return compareTieBreak(left, right);
}

/** Stable sorted copy — never mutates `rows`. */
export function sortMyBookings(
  rows: readonly NativeBooking[],
  sort: MyBookingsSortState,
  siteName: (row: NativeBooking) => string,
  equipmentName: (row: NativeBooking) => string,
): NativeBooking[] {
  return [...rows].sort((left, right) =>
    compareMyBookings(left, right, sort, siteName, equipmentName),
  );
}

export function nextMyBookingsSort(
  current: MyBookingsSortState,
  key: MyBookingsSortKey,
): MyBookingsSortState {
  if (current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: 'asc' };
}

export function ariaSortValue(
  current: MyBookingsSortState,
  key: MyBookingsSortKey,
): 'ascending' | 'descending' | 'none' {
  if (current.key !== key) {
    return 'none';
  }
  return current.dir === 'asc' ? 'ascending' : 'descending';
}
